<template>
  <fts-client-modal
    :append-to-body="appendToBody"
    :width="'90%'"
    dynamic-screen
    :footer="false"
    append-content-class="d-widget-chart-modal__body"
    class="d-widget-chart-modal"
    ref="chartModal"
    :data-widget-load="loading ? 'loading' : 'load'"
    @closed="reset"
  >
    <!-- title -->
    <template #title>
      <fts-widget-title
        :modules="modules"
        :module-id="item.moduleId"
        :listing-id="item.listingId"
        :widget-name="item.name"
      />
    </template>
    <!-- actions -->
    <template #actions>
      <fts-btn
        text
        icon
        :loading="loading"
        @click="$emit('chart:refresh')"
      >
        <font-awesome-icon :icon="['fas', 'sync-alt']" />
      </fts-btn>
    </template>
    <!-- body -->
    <template #body>
      <div class="d-widget-chart-modal-content">
        <!-- bar chart -->
        <fts-bar-chart
          :stacked="item.type === 'stackedBar'"
          :chart-options="chartOptions"
          :chart-data="chartData"
          v-if="item.type === 'bar' || item.type === 'stackedBar'"
        />
        <!-- horizontal bar chart -->
        <fts-bar-chart
          horizontal
          :stacked="item.type === 'stackedBarHorizontal'"
          :chart-options="chartOptions"
          :chart-data="chartData"
          v-if="item.type === 'barHorizontal' || item.type === 'stackedBarHorizontal'"
        />
        <!-- pie chart -->
        <fts-pie-chart
          :chart-options="chartOptions"
          :chart-data="chartData"
          v-else-if="item.type === 'pie'"
        />
        <!-- line chart -->
        <fts-line-chart
          :chart-options="chartOptions"
          :chart-data="chartData"
          v-else-if="item.type === 'line'"
        />
        <!-- doughnut chart -->
        <fts-doughnut-chart
          :chart-options="chartOptions"
          :chart-data="chartData"
          v-else-if="item.type === 'doughnut'"
        />
      </div>
      <div class="d-widget-chart-modal-footer">
        <!-- aggregate -->
        <div class="d-widget-chart-aggregate" v-if="chartData && !item.noTotal && (chartData.total || chartData.total === 0)">
          Total {{item.aggregate}}
        </div>
        <!-- summary -->
        <div class="d-widget-chart-summary">
          <template v-if="chartData && !item.noTotal && (chartData.total || chartData.total === 0)">
            <!-- total -->
            <fts-tippy :content="getFormatNumber()" class="d-widget-chart-total">
              <template #trigger>{{getAbbreviateNumber()}}</template>
            </fts-tippy>
            <!-- information -->
            <div class="d-widget-chart-information" v-if="item.displayName">
              <i class="d-widget-chart-information-icon" :class="getInformationIcon()" v-if="getInformationIcon()"></i>
              <div class="d-widget-chart-information-name">{{item.displayName}}</div>
            </div>
          </template>
          <!-- last update -->
          <div class="d-widget-chart-last-update" v-if="lastUpdated">
            last updated
            <fts-tippy
              :content="moment(lastUpdated).format('MMM D, h:mm:ss A')"
              class="d-widget-chart-last-update-date"
            >
              <span slot="trigger">
                {{lastUpdatedHumanize}}
              </span>
            </fts-tippy>
          </div>
        </div>
      </div>    
    </template>
  </fts-client-modal>
</template>

<script>
import moment from "moment";

import commons from "../../commons/commons";

import FTSWidgetTitle from "../FTSWidgetTitle";
import FTSClientModal from "../../elements/FTSClientModal";
import FTSBarChart from "../../elements/chart/FTSBarChart";
import FTSPieChart from "../../elements/chart/FTSPieChart";
import FTSLineChart from "../../elements/chart/FTSLineChart";
import FTSDoughnutChart from "../../elements/chart/FTSDoughnutChart";
import FTSBtn from "../../elements/FTSBtn";
import FTSTippy from "../../elements/FTSTippy";

export default {
  components: {
    "fts-widget-title": FTSWidgetTitle,
    "fts-client-modal": FTSClientModal,
    "fts-bar-chart": FTSBarChart,
    "fts-pie-chart": FTSPieChart,
    "fts-line-chart": FTSLineChart,
    "fts-doughnut-chart": FTSDoughnutChart,
    "fts-btn": FTSBtn,
    "fts-tippy": FTSTippy,
  },
  props: {
    appendToBody: Boolean,
    modules: Array,
    item: Object,
    chartData: Object,
    chartOptions: Object,
    lastUpdated: Object,
    lastUpdatedHumanize: String,
    loading: Boolean,
  },
  data: () => ({
    resolve: null,
  }),
  methods: {
    show() {
      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.chartModal.show();
      });
    },
    reset() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    moment,
    getFormatNumber() {
      return commons.formatNumber(
        this.chartData.total,
        {
          currency: Boolean(this.item.informationType && this.item.informationType === 4),
        }
      );
    },
    getAbbreviateNumber() {
      return commons.formatNumber(
        this.chartData.total,
        {
          currency: Boolean(this.item.informationType && this.item.informationType === 4),
          abbreviate: true,
        }
      );
    },
    getInformationIcon() {
      return commons.informationIcon(this.item.informationType);
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.d-widget-chart-modal {
  .d-widget-chart-modal__body {
    display: flex;
    flex-direction: column;
  }
  .d-widget-chart-modal-content {
    flex: 1;
  }
  .d-widget-chart-modal-footer {
    padding-top: 1rem;
    .d-widget-chart-aggregate {
      margin-bottom: 0.25rem;
      font-size: 0.875rem;
      color: var(--fts-default);
      text-transform: capitalize;
    }
    .d-widget-chart-summary {
      width: 100%;
      display: flex;
      align-items: flex-end;
      cursor: default;
    }
    .d-widget-chart-total {
      font-weight: 500;
      font-size: 1.5rem;
      line-height: 1.5rem;
      cursor: default;
    }
    .d-widget-chart-information {
      min-width: 0;
      display: flex;
      align-items: center;
      margin-left: 0.5rem;
      font-size: 0.875rem;
      .d-widget-chart-information-icon {
        margin-right: 0.125rem;
        color: var(--fts-secondary);
      }
      .d-widget-chart-information-name {
        @include text-overflow-ellipsis;
      }
    }
    .d-widget-chart-last-update {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-left: 1rem;
      white-space: nowrap;
      font-size: 0.875rem;
      color: var(--fts-default);
      .d-widget-chart-last-update-date {
        margin-left: 0.25rem;
        font-size: 0.875rem;
      }
    }
  }
}
</style>